import {
  html,
  attr,
  prop,
  Value,
  When,
  on,
  Signal,
  ForEach,
  Fragment,
  Async,
} from '@tempots/dom'
import { Anchor, NavigationService } from '@tempots/ui'
import { Button, ButtonVariant, Icon, TextInput, Modal } from '@tempots/beatui'
import { PageLayout } from '@/components/layout'
import { LoadingState, ErrorState, ConfirmDialog, toast } from '@/components/ui'
import {
  createProjectPersistentFiles,
  createProjectFolders,
} from '@/services/gateway'
import type { PersistentFilesService } from '@/services/persistent-files.service'
import type { FoldersService } from '@/services/folders.service'
import {
  validateFilename,
  getErrorMessage,
  isFilenameExistsError,
  isApiError,
} from '@/services/errors'
import type { FileResponse } from '@/services/persistent-files.service'
import type { FolderResponse } from '@/services/types'
import { Workspace } from '@/state/workspace'
import {
  formatBytes,
  formatDate,
  getFileTypeFromMime,
  getFileIcon,
  getFileIconBg,
} from '@/utils'

// ============================================================================
// File Detail Page
// ============================================================================

export interface FileDetailPageOptions {
  projectId: Signal<string>
  id: Signal<string>
}

export function FileDetailPage({ projectId, id }: FileDetailPageOptions) {
  projectId.on(Workspace.changeProject)

  // Create project-scoped services (memoized)
  let persistentFilesService: PersistentFilesService | null = null
  let foldersService: FoldersService | null = null
  let lastProjectId = ''

  const getServices = () => {
    const pid = projectId.value
    if (!pid) {
      return { persistentFiles: null, folders: null }
    }
    if (pid !== lastProjectId) {
      persistentFilesService = createProjectPersistentFiles(pid)
      foldersService = createProjectFolders(pid)
      lastProjectId = pid
    }
    return { persistentFiles: persistentFilesService, folders: foldersService }
  }

  const file = prop<FileResponse | null>(null)

  // Rename state
  const isEditing = prop(false)
  const editedName = prop('')
  const renameError = prop<string | null>(null)
  const renaming = prop(false)

  // Delete state
  const deleting = prop(false)

  // Move to folder state
  const availableFolders = prop<FolderResponse[]>([])
  const loadingFolders = prop(false)
  const moving = prop(false)
  const moveError = prop<string | null>(null)

  // Derived boolean states for When conditions
  const hasRenameError = renameError.map((e) => e !== null)
  const hasMoveError = moveError.map((e) => e !== null)
  const hasInspection = file.map((f) => f?.inspection !== undefined)
  const hasPageCount = file.map((f) => f?.inspection?.pageCount !== undefined)
  // Duration is stored in metadata if available
  const hasDuration = file.map(
    (f) => (f?.inspection?.metadata?.duration ?? undefined) !== undefined
  )
  const currentFolderName = prop('Root (All Files)')

  // Load folder name when file changes
  file.on(async (f) => {
    if (!f?.folderId) {
      currentFolderName.set('Root (All Files)')
      return
    }

    const { folders } = getServices()
    if (!folders) {
      currentFolderName.set(f.folderId)
      return
    }

    try {
      const folder = await folders.get(f.folderId)
      currentFolderName.set(folder.name)
    } catch {
      currentFolderName.set(f.folderId)
    }
  })

  // Get the file hash from the id (handle both Value<string> and string)
  const getHash = () => (typeof id === 'string' ? id : id.value)

  // Load file details
  const loadFile = async () => {
    const { persistentFiles } = getServices()
    if (!persistentFiles) {
      throw new Error('No project selected')
    }

    const hash = getHash()
    const response = await persistentFiles.get(hash)
    file.set(response)
    editedName.set(response.filename)
    return response
  }

  // Initial load promise
  const initialLoad = loadFile()

  // Handle rename
  const handleRename = async () => {
    const currentFile = file.get()
    const newName = editedName.get().trim()

    if (!currentFile || !newName || newName === currentFile.filename) {
      isEditing.set(false)
      return
    }

    // Client-side validation
    const validationError = validateFilename(newName)
    if (validationError) {
      renameError.set(validationError)
      return
    }

    const { persistentFiles } = getServices()
    if (!persistentFiles) {
      renameError.set('No project selected')
      return
    }

    try {
      renaming.set(true)
      renameError.set(null)
      const updated = await persistentFiles.update(currentFile.hash, {
        newFilename: newName,
      })
      file.set(updated)
      isEditing.set(false)
    } catch (e) {
      // Provide user-friendly error message
      if (isApiError(e) && isFilenameExistsError(e)) {
        renameError.set(
          'A file with this name already exists. Please choose a different name.'
        )
      } else {
        renameError.set(getErrorMessage(e))
      }
    } finally {
      renaming.set(false)
    }
  }

  // Handle delete (called from ConfirmDialog)
  const handleDelete = async () => {
    const currentFile = file.get()
    if (!currentFile) return

    const { persistentFiles } = getServices()
    if (!persistentFiles) {
      toast.error('No project selected')
      return
    }

    try {
      deleting.set(true)
      await persistentFiles.delete(currentFile.hash)
      toast.success('File deleted')
      NavigationService.navigate(
        Workspace.projectUrl('/files', projectId.value)!
      )
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Delete failed')
      deleting.set(false)
    }
  }

  // Load all folders for move dialog
  const loadFolders = async () => {
    const { folders } = getServices()
    if (!folders) {
      return
    }

    try {
      loadingFolders.set(true)
      const response = await folders.list({ limit: 100 })
      availableFolders.set(response.folders)
    } catch {
      // Ignore errors loading folders
    } finally {
      loadingFolders.set(false)
    }
  }

  // Handle move to folder
  const handleMoveToFolder = async (folderId: string) => {
    const currentFile = file.get()
    if (!currentFile) return

    const { persistentFiles, folders } = getServices()
    if (!persistentFiles) {
      moveError.set('No project selected')
      return
    }

    try {
      moving.set(true)
      moveError.set(null)
      const updated = await persistentFiles.update(currentFile.hash, {
        folderId,
      })
      file.set(updated)

      // Get folder name for success message
      let folderName = 'Root (All Files)'
      if (folderId && folders) {
        try {
          const folder = await folders.get(folderId)
          folderName = folder.name
        } catch {
          folderName = folderId
        }
      }
      toast.success(`File moved to ${folderName}`)
    } catch (e) {
      moveError.set(getErrorMessage(e))
    } finally {
      moving.set(false)
    }
  }

  // Helper to get download URL
  // Use getContentUrl for synchronous URL generation (direct content streaming)
  const getDownloadUrl = (hash: string): string => {
    const { persistentFiles } = getServices()
    if (!persistentFiles) {
      return '#'
    }
    return persistentFiles.getContentUrl(hash)
  }

  return PageLayout({
    header: html.div(
      attr.class('flex items-center gap-3'),
      Anchor(
        {
          href: projectId.map(
            (pid) => Workspace.projectUrl('/files', pid) ?? '#'
          ),
        },
        attr.class('text-gray-400 hover:text-gray-600'),
        Icon({ icon: 'mdi:chevron-left' }, attr.class('w-5 h-5'))
      ),
      // File name (editable)
      When(
        isEditing,
        () =>
          html.div(
            attr.class('flex items-center gap-2'),
            TextInput({
              value: editedName,
              onInput: (v) => editedName.set(v),
              autofocus: true,
            }),
            ConfirmDialog(
              {
                title: 'Rename File',
                message: 'Are you sure you want to rename this file?',
                confirmText: 'Rename',
                variant: 'info',
                onConfirm: handleRename,
              },
              Button(
                {
                  size: 'sm',
                  disabled: renaming,
                },
                renaming.map((r) => (r ? 'Saving...' : 'Save'))
              )
            ),
            Button(
              {
                size: 'sm',
                variant: 'ghost' as ButtonVariant,
                onClick: () => {
                  isEditing.set(false)
                  const currentFile = file.get()
                  if (currentFile) {
                    editedName.set(currentFile.filename)
                  }
                },
              },
              'Cancel'
            )
          ),
        () => html.span(file.map((f) => f?.filename ?? 'Loading...'))
      )
    ),
    content: Async(initialLoad, {
      pending: () => LoadingState({ message: 'Loading file...', size: 'lg' }),
      error: (err) =>
        ErrorState({
          error: prop<string | null>(
            err instanceof Error ? err.message : 'Failed to load file'
          ),
        }),
      then: () =>
        Fragment(
          // Rename error
          When(hasRenameError, () =>
            ErrorState({ error: renameError, title: 'Rename failed' })
          ),
          // Content
          html.div(
            attr.class('space-y-6'),
            // Preview and Actions grid
            html.div(
              attr.class('grid grid-cols-1 lg:grid-cols-2 gap-6'),
              // Preview
              FilePreview(file as Signal<FileResponse | null>, getDownloadUrl),
              // Actions
              html.div(
                attr.class('bg-white rounded-xl border border-gray-200 p-6'),
                html.h2(
                  attr.class('text-base font-semibold text-gray-900 mb-4'),
                  'Actions'
                ),
                html.div(
                  attr.class('space-y-3'),
                  // Download button (as link)
                  html.a(
                    attr.href(
                      file.map((f) => (f ? getDownloadUrl(f.hash) : '#'))
                    ),
                    attr.class(
                      'flex items-center gap-3 px-4 py-3 border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors text-left'
                    ),
                    attr.download(file.map((f) => f?.filename ?? '')),
                    Icon(
                      { icon: 'mdi:download' },
                      attr.class('w-5 h-5 text-gray-400')
                    ),
                    html.div(
                      html.p(
                        attr.class('text-sm font-medium text-gray-900'),
                        'Download'
                      ),
                      html.p(
                        attr.class('text-xs text-gray-500'),
                        'Save file to your computer'
                      )
                    )
                  ),
                  // Rename button
                  html.button(
                    attr.type('button'),
                    attr.class(
                      'w-full flex items-center gap-3 px-4 py-3 border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors text-left'
                    ),
                    on.click(() => isEditing.set(true)),
                    Icon(
                      { icon: 'mdi:pencil' },
                      attr.class('w-5 h-5 text-gray-400')
                    ),
                    html.div(
                      html.p(
                        attr.class('text-sm font-medium text-gray-900'),
                        'Rename'
                      ),
                      html.p(
                        attr.class('text-xs text-gray-500'),
                        'Change the filename'
                      )
                    )
                  ),
                  // Move to folder modal
                  Modal(
                    { size: 'md', dismissable: true },
                    (openModal, closeModal) =>
                      html.div(
                        // Trigger button
                        html.button(
                          attr.type('button'),
                          attr.class(
                            'w-full flex items-center gap-3 px-4 py-3 border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors text-left'
                          ),
                          on.click(() => {
                            moveError.set(null)
                            loadFolders()
                            openModal({
                              header: html.h3(
                                attr.class(
                                  'text-lg font-semibold text-gray-900'
                                ),
                                'Move to Folder'
                              ),
                              body: html.div(
                                // Error message
                                When(hasMoveError, () =>
                                  html.div(
                                    attr.class('mb-4'),
                                    ErrorState({
                                      error: moveError,
                                      title: 'Move failed',
                                    })
                                  )
                                ),
                                // Loading
                                When(loadingFolders, () =>
                                  LoadingState({ size: 'md' })
                                ),
                                // Folder list
                                When(
                                  loadingFolders.map((l) => !l),
                                  () =>
                                    html.div(
                                      attr.class('space-y-2'),
                                      // Root option
                                      html.button(
                                        attr.type('button'),
                                        attr.class(
                                          moving.map((m) =>
                                            m
                                              ? 'w-full flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg text-left opacity-50 cursor-not-allowed'
                                              : 'w-full flex items-center gap-3 px-4 py-3 border border-gray-200 hover:bg-gray-50 rounded-lg text-left transition-colors'
                                          )
                                        ),
                                        attr.disabled(moving),
                                        on.click(() => {
                                          handleMoveToFolder('')
                                          closeModal()
                                        }),
                                        Icon(
                                          { icon: 'mdi:folder-home' },
                                          attr.class('w-5 h-5 text-gray-400')
                                        ),
                                        html.span(
                                          attr.class(
                                            'text-sm font-medium text-gray-900'
                                          ),
                                          'Root (All Files)'
                                        )
                                      ),
                                      // Folder options
                                      ForEach(availableFolders, (folder) =>
                                        html.button(
                                          attr.type('button'),
                                          attr.class(
                                            moving.map((m) =>
                                              m
                                                ? 'w-full flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg text-left opacity-50 cursor-not-allowed'
                                                : 'w-full flex items-center gap-3 px-4 py-3 border border-gray-200 hover:bg-gray-50 rounded-lg text-left transition-colors'
                                            )
                                          ),
                                          attr.disabled(moving),
                                          on.click(() => {
                                            handleMoveToFolder(folder.get().id)
                                            closeModal()
                                          }),
                                          Icon(
                                            { icon: 'mdi:folder' },
                                            attr.class('w-5 h-5 text-gray-400')
                                          ),
                                          html.span(
                                            attr.class(
                                              'text-sm font-medium text-gray-900'
                                            ),
                                            folder.map((f) => f.name)
                                          )
                                        )
                                      )
                                    )
                                )
                              ),
                              footer: html.div(
                                attr.class('flex justify-end'),
                                Button(
                                  {
                                    variant: 'ghost' as ButtonVariant,
                                    onClick: closeModal,
                                  },
                                  'Cancel'
                                )
                              ),
                            })
                          }),
                          Icon(
                            { icon: 'mdi:folder-move' },
                            attr.class('w-5 h-5 text-gray-400')
                          ),
                          html.div(
                            html.p(
                              attr.class('text-sm font-medium text-gray-900'),
                              'Move to Folder'
                            ),
                            html.p(
                              attr.class('text-xs text-gray-500'),
                              'Currently in: ',
                              currentFolderName
                            )
                          )
                        )
                      )
                  ),
                  // Process with Action
                  Anchor(
                    {
                      href: projectId.map(
                        (pid) => Workspace.projectUrl('/actions', pid) ?? '#'
                      ),
                    },
                    attr.class(
                      'flex items-center gap-3 px-4 py-3 border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors text-left'
                    ),
                    Icon(
                      { icon: 'mdi:lightning-bolt' },
                      attr.class('w-5 h-5 text-gray-400')
                    ),
                    html.div(
                      html.p(
                        attr.class('text-sm font-medium text-gray-900'),
                        'Process with Action'
                      ),
                      html.p(
                        attr.class('text-xs text-gray-500'),
                        'Run an action on this file'
                      )
                    )
                  ),
                  // Delete button
                  ConfirmDialog(
                    {
                      title: 'Delete File',
                      message: file.map((f) =>
                        f
                          ? `Are you sure you want to delete "${f.filename}"? This action cannot be undone.`
                          : 'Are you sure you want to delete this file?'
                      ),
                      confirmText: 'Delete',
                      variant: 'danger',
                      onConfirm: handleDelete,
                    },
                    html.button(
                      attr.type('button'),
                      attr.class(
                        'w-full flex items-center gap-3 px-4 py-3 border border-red-200 hover:bg-red-50 rounded-lg transition-colors text-left'
                      ),
                      attr.disabled(deleting),
                      Icon(
                        {
                          icon: deleting.map((d): string =>
                            d ? 'mdi:loading' : 'mdi:delete'
                          ),
                        },
                        attr.class(
                          deleting.map((d) =>
                            d
                              ? 'w-5 h-5 text-red-400 animate-spin'
                              : 'w-5 h-5 text-red-400'
                          )
                        )
                      ),
                      html.div(
                        html.p(
                          attr.class('text-sm font-medium text-red-600'),
                          deleting.map((d) => (d ? 'Deleting...' : 'Delete'))
                        ),
                        html.p(
                          attr.class('text-xs text-red-400'),
                          'Permanently remove this file'
                        )
                      )
                    )
                  )
                )
              )
            ),
            // Metadata
            html.div(
              attr.class('bg-white rounded-xl border border-gray-200 p-6'),
              html.h2(
                attr.class('text-base font-semibold text-gray-900 mb-4'),
                'File Metadata'
              ),
              html.dl(
                attr.class(
                  'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
                ),
                MetadataItem(
                  'Filename',
                  file.map((f) => f?.filename ?? '-')
                ),
                MetadataItem(
                  'MIME Type',
                  file.map((f) => f?.mimeType ?? '-')
                ),
                MetadataItem(
                  'Size',
                  file.map((f) => (f ? formatBytes(f.size) : '-'))
                ),
                MetadataItem(
                  'Dimensions',
                  file.map((f) => {
                    const dims = f?.inspection?.dimensions
                    if (dims?.width && dims?.height) {
                      return `${dims.width} × ${dims.height} px`
                    }
                    return '-'
                  })
                ),
                MetadataItem(
                  'Created',
                  file.map((f) => (f ? formatDate(f.createdAt) : '-'))
                ),
                MetadataItem(
                  'File Reference',
                  file.map((f) => f?.fileRef ?? '-'),
                  true
                )
              )
            ),
            // Inspection Results (if available)
            When(hasInspection, () =>
              html.div(
                attr.class('bg-white rounded-xl border border-gray-200 p-6'),
                html.h2(
                  attr.class('text-base font-semibold text-gray-900 mb-4'),
                  'Inspection Results'
                ),
                html.dl(
                  attr.class(
                    'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6'
                  ),
                  MetadataItem(
                    'Detected Type',
                    file.map((f) => f?.inspection?.contentType ?? '-')
                  ),
                  MetadataItem(
                    'Dimensions',
                    file.map((f) => {
                      const dims = f?.inspection?.dimensions
                      if (dims?.width && dims?.height) {
                        return `${dims.width} × ${dims.height}`
                      }
                      return '-'
                    })
                  ),
                  When(hasPageCount, () =>
                    MetadataItem(
                      'Page Count',
                      // eslint-disable-next-line tempots/no-renderable-signal-map -- returns string, not renderable
                      file.map((f) => String(f?.inspection?.pageCount ?? '-'))
                    )
                  ),
                  When(hasDuration, () =>
                    MetadataItem(
                      'Duration',
                      file.map(
                        (f) =>
                          `${(f?.inspection?.metadata?.duration as number) ?? 0}s`
                      )
                    )
                  ),
                  MetadataItem(
                    'Extension',
                    file.map((f) => f?.inspection?.extension ?? '-')
                  )
                )
              )
            )
          )
        ),
    }),
  })
}

// ============================================================================
// Helper Components
// ============================================================================

function FilePreview(
  file: Signal<FileResponse | null>,
  getDownloadUrl: (hash: string) => string
) {
  const isImage = file.map((f) => f !== null && f.mimeType.startsWith('image/'))

  return html.div(
    attr.class('bg-white rounded-xl border border-gray-200 p-6'),
    html.h2(
      attr.class('text-base font-semibold text-gray-900 mb-4'),
      'Preview'
    ),
    html.div(
      attr.class(
        'bg-gray-100 rounded-lg aspect-video flex items-center justify-center overflow-hidden'
      ),
      When(
        isImage,
        () =>
          html.img(
            attr.src(file.map((f) => (f ? getDownloadUrl(f.hash) : ''))),
            attr.alt(file.map((f) => f?.filename ?? '')),
            attr.class('max-w-full max-h-full object-contain')
          ),
        () =>
          html.div(
            attr.class(
              file.map((f) => {
                const fileType = f
                  ? getFileTypeFromMime(f.mimeType)
                  : 'document'
                return `w-full h-full ${getFileIconBg(fileType)} flex items-center justify-center`
              })
            ),
            Icon(
              {
                icon: file.map((f) => {
                  const fileType = f
                    ? getFileTypeFromMime(f.mimeType)
                    : 'document'
                  return getFileIcon(fileType)
                }),
              },
              attr.class('w-16 h-16 opacity-50')
            )
          )
      )
    )
  )
}

function MetadataItem(label: string, value: Value<string>, mono = false) {
  return html.div(
    html.dt(attr.class('text-sm font-medium text-gray-500'), label),
    html.dd(
      attr.class(
        `mt-1 text-sm text-gray-900 ${mono ? 'font-mono text-xs break-all' : ''}`
      ),
      value
    )
  )
}
